// "You may like this" carousel for the cart drawer (used by snippets/cart-recommendations.liquid)

// Recommendations are cached per set of cart products so the carousel
// re-appears instantly after the drawer re-renders.
const cache = new Map();

const formatMoney = (cents, format) => {
  const value = Number(cents) / 100;
  const fmt = (n, decimals, thousands, decimal) => {
    const [int, dec] = n.toFixed(decimals).split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? withSep + decimal + dec : withSep;
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
      default: return fmt(value, 2, ',', '.');
    }
  });
};

const escapeHtml = (str) =>
  String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const sizedImage = (src, width) => {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
};

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // The cart drawer re-renders (morphs) its contents after every cart change.
    // Watch for that and put the recommendations back.
    this.observer = new MutationObserver(() => this.hydrate());
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-key', 'data-hydrated'],
    });
    this.hydrate();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.boundTrack?.removeEventListener('scroll', this.updateArrows);
    this.boundTrack = null;
  }

  get track() {
    return this.querySelector('.cart-recs__track');
  }

  hydrate() {
    const track = this.track;
    if (!track) return;
    const key = this.dataset.key || '';
    if (track.dataset.hydrated === key) return;

    // Server-rendered cards = fallback collection
    const fallbackCards = [...track.children].map((node) => node.cloneNode(true));
    const seeds = (this.dataset.seedIds || '').split(',').filter(Boolean);

    if (!seeds.length) {
      this.paint(track, key, [], fallbackCards);
      return;
    }

    if (!cache.has(key)) cache.set(key, this.fetchRecommendations(seeds));
    const pending = cache.get(key);

    if (pending.resolved) {
      this.paint(track, key, pending.resolved, fallbackCards);
    } else {
      // Show fallback right away, swap in recommendations when they arrive
      this.paint(track, key, [], fallbackCards, true);
      pending.then((products) => {
        if (this.track === track && (this.dataset.key || '') === key) {
          this.paint(track, key, products, fallbackCards);
        }
      });
    }
  }

  fetchRecommendations(seeds) {
    const limit = Number(this.dataset.limit) || 8;
    const requests = seeds.map((id) =>
      fetch(`${this.dataset.recommendationsUrl}?product_id=${id}&limit=${limit}&intent=related`)
        .then((res) => (res.ok ? res.json() : { products: [] }))
        .then((data) => data.products || [])
        .catch(() => [])
    );
    const promise = Promise.all(requests).then((lists) => {
      // Interleave results so each cart item contributes suggestions
      const merged = [];
      const longest = Math.max(0, ...lists.map((l) => l.length));
      for (let i = 0; i < longest; i++) lists.forEach((list) => list[i] && merged.push(list[i]));
      promise.resolved = merged;
      return merged;
    });
    return promise;
  }

  paint(track, key, products, fallbackCards, pendingRecommendations = false) {
    const limit = Number(this.dataset.limit) || 8;
    const exclude = new Set((this.dataset.cartIds || '').split(',').filter(Boolean));
    const seen = new Set();
    const nodes = [];

    for (const product of products) {
      if (nodes.length >= limit) break;
      const id = String(product.id);
      if (exclude.has(id) || seen.has(id) || product.available === false) continue;
      const card = this.buildCard(product);
      if (!card) continue;
      seen.add(id);
      nodes.push(card);
    }

    for (const card of fallbackCards) {
      if (nodes.length >= limit) break;
      const id = card.dataset?.productId;
      if (!id || exclude.has(id) || seen.has(id)) continue;
      seen.add(id);
      nodes.push(card.cloneNode(true));
    }

    this.observer?.disconnect();
    track.replaceChildren(...nodes);
    track.dataset.hydrated = pendingRecommendations ? '' : key;
    if (!pendingRecommendations) track.scrollLeft = 0;
    this.hidden = nodes.length === 0;
    this.bindTrack();
    requestAnimationFrame(this.updateArrows);
    this.observer?.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-key', 'data-hydrated'],
    });
  }

  buildCard(product) {
    const variant = (product.variants || []).find((v) => v.available) || null;
    if (!variant) return null;

    const format = this.dataset.moneyFormat;
    const price = variant.price;
    const compare = variant.compare_at_price;
    const onSale = compare && compare > price;
    const off = onSale ? Math.round(((compare - price) * 100) / compare) : 0;
    const url = product.url || `/products/${product.handle}`;
    const image = product.featured_image || product.images?.[0];
    const title = escapeHtml(product.title);

    const li = document.createElement('li');
    li.className = 'cart-recs__card';
    li.dataset.productId = product.id;
    li.innerHTML = `
      <a class="cart-recs__media" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${escapeHtml(sizedImage(image, 240))}" alt="${title}" width="120" height="120" loading="lazy">` : ''}
        ${onSale ? `<span class="cart-recs__badge">${off}% off</span>` : ''}
      </a>
      <div class="cart-recs__info">
        <a class="cart-recs__title" href="${escapeHtml(url)}">${title}</a>
        <div class="cart-recs__prices">
          ${onSale ? `<s class="cart-recs__compare">${escapeHtml(formatMoney(compare, format))}</s>` : ''}
          <span class="cart-recs__price">${escapeHtml(formatMoney(price, format))}</span>
        </div>
        <button type="button" class="cart-recs__add" data-variant-id="${variant.id}" aria-label="Add ${title} to cart">+ Add</button>
      </div>`;
    return li;
  }

  onClick = (event) => {
    const arrow = event.target.closest('.cart-recs__arrow');
    if (arrow) return this.scrollByCard(Number(arrow.dataset.direction));
    const button = event.target.closest('.cart-recs__add');
    if (button) this.addToCart(button);
  };

  scrollByCard(direction) {
    const track = this.track;
    const card = track?.querySelector('.cart-recs__card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  updateArrows = () => {
    const track = this.track;
    const nav = this.querySelector('.cart-recs__nav');
    if (!track || !nav) return;
    const max = track.scrollWidth - track.clientWidth;
    nav.hidden = max <= 2;
    const [prev, next] = nav.querySelectorAll('.cart-recs__arrow');
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  };

  bindTrack() {
    const track = this.track;
    if (!track || track === this.boundTrack) return;
    this.boundTrack?.removeEventListener('scroll', this.updateArrows);
    track.addEventListener('scroll', this.updateArrows, { passive: true });
    this.boundTrack = track;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.resizeObserver.observe(track);
  }

  async addToCart(button) {
    if (button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding…';

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add');

      const productId = button.closest('[data-product-id]')?.dataset.productId;
      const detail = {
        source: 'product-form-component',
        itemCount: 1,
        productId,
        sections: data.sections,
      };

      // Tell Horizon the cart changed so the drawer + cart bubble refresh.
      let event;
      try {
        const { CartAddEvent } = await import('@theme/events');
        event = new CartAddEvent({}, String(button.dataset.variantId), detail);
      } catch (e) {
        event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: {}, sourceId: String(button.dataset.variantId), data: detail },
        });
      }
      this.dispatchEvent(event);

      button.textContent = 'Added ✓';
    } catch (error) {
      button.textContent = 'Sold out';
      console.error('[cart-recommendations]', error);
    } finally {
      setTimeout(() => {
        if (!button.isConnected) return;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.textContent = label;
      }, 1500);
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}

// ---------------------------------------------------------------------------
// Offer progress bar (snippets/cart-offer-bar.liquid)
// The drawer re-renders its HTML on every cart change, which would make the
// bar jump. This slides the fill from where it was to the new value, replays
// it each time the drawer opens, and pops each step as the fill reaches it.
// ---------------------------------------------------------------------------
const OFFER_FILL_MS = 800;
let lastOfferFill = null;
let lastOfferUnlocked = null;

class CartOfferBar extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timers = [];
    this.wasVisible = false;

    this.observer = new MutationObserver(() => this.sync());
    this.observe();

    // Replay from 0 whenever the drawer is opened
    this.intersection = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      const dialog = this.closest('dialog');
      if (visible && !this.wasVisible && this.closedSinceLastPlay !== false) {
        this.play(0);
        this.closedSinceLastPlay = false;
      }
      if (!visible && (!dialog || !dialog.open)) this.closedSinceLastPlay = true;
      this.wasVisible = visible;
    });
    this.intersection.observe(this);

    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.intersection?.disconnect();
    this.timers.forEach(clearTimeout);
  }

  observe() {
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-fill', 'data-shown'],
    });
  }

  get fill() {
    return this.querySelector('.cart-offer__fill');
  }

  sync() {
    const fill = this.fill;
    if (!fill) return;
    const target = parseFloat(fill.dataset.fill) || 0;
    if (fill.dataset.shown === String(target)) return;
    this.play(lastOfferFill ?? target);
  }

  play(from) {
    const fill = this.fill;
    if (!fill) return;

    const target = parseFloat(fill.dataset.fill) || 0;
    const steps = [...this.querySelectorAll('.cart-offer__step')];
    const unlocked = steps.filter((step) => step.classList.contains('is-unlocked')).length;
    const previouslyUnlocked = from === 0 ? 0 : lastOfferUnlocked ?? unlocked;

    lastOfferFill = target;
    lastOfferUnlocked = unlocked;

    this.observer.disconnect();
    this.timers.forEach(clearTimeout);
    this.timers = [];
    fill.dataset.shown = String(target);

    if (this.reduceMotion || from === target) {
      fill.style.width = `${target}%`;
      steps.forEach((step) => step.classList.remove('is-pending'));
      this.observe();
      return;
    }

    // Start from the old width without animating, then slide to the new one
    fill.style.transition = 'none';
    fill.style.width = `${from}%`;
    fill.getBoundingClientRect();
    fill.style.transition = '';
    requestAnimationFrame(() => {
      fill.style.width = `${target}%`;
    });

    // Newly unlocked steps light up when the fill reaches them
    steps.forEach((step, index) => {
      if (index < previouslyUnlocked || !step.classList.contains('is-unlocked')) return;
      step.classList.add('is-pending');
      const position = ((index + 0.5) / steps.length) * 100;
      const progress = Math.min(1, Math.max(0, (position - from) / (target - from || 1)));
      // the fill eases out, so it reaches each point earlier than linear time
      const delay = Math.sqrt(progress) * OFFER_FILL_MS * 0.55;
      this.timers.push(
        setTimeout(() => {
          step.classList.remove('is-pending');
          step.classList.add('is-pop');
          this.timers.push(setTimeout(() => step.classList.remove('is-pop'), 450));
        }, delay)
      );
    });

    this.observe();
  }
}

if (!customElements.get('cart-offer-bar')) {
  customElements.define('cart-offer-bar', CartOfferBar);
}
